import React from 'react';
import {useTranslation} from 'react-i18next';
import {useHistory, useLocation} from 'react-router-dom';
import {ErrorBoundary, withProfiler} from '@sentry/react';

import {useAuth} from './context/auth';
import {useReservation} from './context/reservation';
import {SHORT_SHARE_LINK_SEARCH_PARAM_NAME} from './context/storedURLParams';
import {addFocusTypeDetection, getURLSearchParam} from './utils/common';
import Routes, {PATHS, UNIVERSAL_PAGES} from './Routes';

import Modal from './components/Modal';
import ScrollToTop from './utils/scrollToTop';
import Analytics from './components/Analytics';
import WebcamWarningModal from './components/WebcamWarningModal';
import ErrorBoundaryFallback from './components/ErrorBoundaryFallback';
import SplashScreen from './views/SplashScreen';

import errorIcon from './assets/error.svg';
import './styles/app.sass';
import {useRedirectsGlobal} from './hooks';
import { useRedirectAfterIdle } from 'utils/hooks';

export const IS_UNIVERSAL_SESSION_PARAM = 'is-universal-session';
const IS_PRODUCTION_BUILD = process.env.NODE_ENV === 'production';
const HOUSING_SEARCH_PARAM = 'housing-id';
const USER_SEARCH_PARAM = 'user';

function App() {
  const history = useHistory();
  const redirect = useRedirectAfterIdle()
  const location = useLocation();
  const {pathname} = location;
  const {t} = useTranslation();
  const {isLoading: isLoadingToken, isTokenValid} = useAuth();
  const {
    isLoading: isLoadingReservation,
    isError: isReservationError,
    data: reservation,
  } = useReservation();
  const [isLoadingError, setIsLoadingError] = React.useState(false);
  const [isUniversalLink, setIsUniversalLink] = React.useState(false);
  useRedirectsGlobal();


  const isSplashScreenVisible =
    !isUniversalLink && (isLoadingToken || isLoadingReservation) && !reservation.id;

  const isTokenLoadingError = !isLoadingToken && !isTokenValid && !isLoadingError;
  const isReservationLoadingError = isReservationError && !isLoadingError;

  React.useEffect(() => {
    return addFocusTypeDetection();
  });

  React.useEffect(() => {
    isUniversalLink
      ? sessionStorage.setItem(IS_UNIVERSAL_SESSION_PARAM, 'true')
      : sessionStorage.removeItem(IS_UNIVERSAL_SESSION_PARAM);
  }, [isUniversalLink]);
  React.useEffect(
    function handleIsUniversalLinkForSearchReservations() {
      const housingId = getURLSearchParam(HOUSING_SEARCH_PARAM);
      const l = getURLSearchParam(SHORT_SHARE_LINK_SEARCH_PARAM_NAME);
      const user = getURLSearchParam(USER_SEARCH_PARAM);

      if (housingId) {
        setIsUniversalLink(Boolean(housingId));
        sessionStorage.setItem(HOUSING_SEARCH_PARAM, housingId);
        history.replace(PATHS.searchReservation, {housingId, l, user});
      }
    },
    [history],
  );

  React.useEffect(()=>{
    const sessionHousingId = sessionStorage.getItem(HOUSING_SEARCH_PARAM);
    if (sessionHousingId) {
      redirect(sessionHousingId);
    }
  }, [redirect])

  React.useEffect(
    function handleIsUniversalLinkForPages() {
      const isUniversalPage = UNIVERSAL_PAGES.some(page => pathname.includes(page));

      if (isUniversalPage) setIsUniversalLink(isUniversalPage);
    },
    [pathname],
  );

  React.useEffect(() => {
    if (isUniversalLink) return;

    if (isTokenLoadingError || isReservationLoadingError) {
      setIsLoadingError(true);
    }
  }, [isTokenLoadingError, isReservationLoadingError, isUniversalLink]);

  React.useLayoutEffect(
    function handleFirstPaymentsLoad() {
      if (sessionStorage.getItem('firstLoadPayments')) return;

      sessionStorage.setItem('firstLoadPayments', 'true');

      if (pathname !== '/pay/') {
        sessionStorage.setItem('firstLoadPayments', 'false');
      }
    },
    [pathname],
  );

  return (
    <ErrorBoundary
      fallback={<ErrorBoundaryFallback />}
      showDialog={IS_PRODUCTION_BUILD}
      dialogOptions={{
        labelComments: t('describe_what_happened'),
      }}
    >
      <SplashScreen visible={IS_PRODUCTION_BUILD && isSplashScreenVisible}>
        {isLoadingError && (
          <Modal
            open
            title={t('oops')}
            text={
              isReservationLoadingError
                ? t('reservation_loading_error')
                : t('link_expired_loading_error')
            }
            iconSrc={errorIcon}
            iconAlt="Error sign"
          />
        )}
        <WebcamWarningModal />
        <Analytics />
        <ScrollToTop />
        <Routes />
      </SplashScreen>
    </ErrorBoundary>
  );
}

export default withProfiler(App);
